import { PlaceRows } from "../account/places.tsx";
import type { Place } from "@teamna/web-client/lib/places.ts";
import type { MyBusiness } from "@teamna/shared";
import { Link, useRouterState } from "@tanstack/react-router";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ComponentType,
  type ReactNode,
  type RefObject,
  type SVGProps,
} from "react";
import { useFocusTrap } from "../lib/focus-trap.ts";
import { Avatar as PersonAvatar } from "./Avatar.tsx";
import { BrandMark } from "./BrandMark.tsx";
import { BusinessMark } from "./BusinessMark.tsx";
import { ChevronLeftIcon, ChevronRightIcon, CloseIcon, LockIcon, MenuIcon } from "./Icons.tsx";
import { Select } from "./Select.tsx";
import { SignOutRow } from "./Screen.tsx";
import { ToastViewport } from "./Toast.tsx";

/**
 * The frame of the owner office and of Control Center (web-app-v2.md section 3).
 *
 * At 1024px and up: a 260px sidebar with the brand mark, the active business and its switcher,
 * the navigation groups, and the account with sign out at the bottom. The button next to the
 * brand collapses it to a 68px rail of icons (2026-10-21); the browser remembers the choice. The content beside it is
 * one column up to 1200px wide that scrolls with the window, or the full width for a page that
 * marks its root `data-page-width="full"` (the Calendar, 2026-10-21). Below 1024px: a top bar (menu,
 * business name, avatar) and a drawer that holds the same navigation. There is no bottom tab bar
 * here; that stays the member area's.
 *
 * One component for both, so Control Center is the same shell with a different nav and a
 * different accent (`useRoleTheme("admin")`) and a dark sidebar (`sidebarTone="dark"`), and a
 * founder with both open can still tell them apart by colour.
 *
 * Every navigation item is a real link: the keyboard reaches all of them, the middle click opens
 * a tab, and a screen reader gets a "Primary" landmark. The drawer closes on Escape, on a
 * navigation, and on the backdrop, and puts focus back on the menu button.
 */

export type ShellNavItem = {
  to: string;
  label: string;
  icon: ComponentType<SVGProps<SVGSVGElement> & { size?: number }>;
  /** Unread count, drawn after the label. Zero and undefined both draw nothing. */
  badge?: number;
  /** A page the business does not have yet (a package to add): a small lock after the label. */
  locked?: boolean;
  /** Other pages that light this item: pages that open from it but keep their own address. */
  also?: string[];
};

export type ShellNavGroup = {
  /** The group's eyebrow. Omitted for the top group (Home). */
  label?: string;
  items: ShellNavItem[];
};

export type ShellBusiness = {
  active: MyBusiness | null;
  /** Every business this account can switch to. The switcher only shows for two or more. */
  options: MyBusiness[];
  onSelect: (businessId: string) => void;
};

export type ShellUser = {
  name: string;
  /** The phone number or the email, under the name. */
  detail: string | null;
  /** Your own profile photo at the open business (migration 0029). Initials when missing. */
  photoUrl?: string | null;
};

/** The places of the account (owner office, coach day, parent home), for the account menu. */
export type ShellPlaces = {
  items: Place[];
  currentId: string | null;
  onPick: (place: Place) => void;
};

/** The browser remembers a collapsed sidebar. A choice of this person on this device only. */
const COLLAPSED_KEY = "teamna-sidebar-collapsed";

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}

function writeCollapsed(collapsed: boolean): void {
  try {
    if (collapsed) localStorage.setItem(COLLAPSED_KEY, "1");
    else localStorage.removeItem(COLLAPSED_KEY);
  } catch {
    // Private mode or blocked storage: the sidebar opens wide next time, which is harmless.
  }
}

export type ShellSignOut = {
  onSignOut: () => void;
  subtitle: string;
};

export function AppShell({
  brand,
  business,
  groups,
  bottom,
  user,
  banner,
  signOut,
  places,
  sidebarTone = "light",
  navExtra,
  children,
}: {
  /** "Owner office" or "Control Center", under the mark. */
  brand: string;
  business?: ShellBusiness;
  groups: ShellNavGroup[];
  /** Settings and the like, pinned above the account block. */
  bottom: ShellNavItem[];
  user: ShellUser | null;
  /**
   * A strip above every page, at every width. Control Center puts "Acting as <business>" here, so
   * nobody forgets whose office they are in.
   */
  banner?: ReactNode;
  /** Replaces the Desk sign out (clear the token) with the area's own. */
  signOut?: ShellSignOut;
  /** Two or more places: the account block opens a "Switch to" menu. */
  places?: ShellPlaces;
  /**
   * "dark" paints the sidebar and the drawer with the `[data-tone="dark"]` tokens in styles.css.
   * The top bar and the page keep the area's light theme. Control Center uses it.
   */
  sidebarTone?: "light" | "dark";
  /**
   * A card under the menu, above Settings: the owner setup progress while the business is new. A
   * link in it closes the drawer on a phone, like a menu link.
   */
  navExtra?: ReactNode;
  children: ReactNode;
}) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const menuButton = useRef<HTMLButtonElement>(null);

  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  const toggleCollapsed = useCallback(() => {
    setCollapsed((value) => {
      writeCollapsed(!value);
      return !value;
    });
  }, []);

  const title = business?.active?.name ?? brand;

  return (
    <div className="flex min-h-dvh [--sticky-top:var(--topbar-height)] [--tabbar-height:0px] lg:[--sticky-top:0px]">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[70] focus:rounded-lg focus:bg-paper focus:px-3 focus:py-2 focus:text-[14px] focus:font-semibold focus:shadow-card"
      >
        Skip to content
      </a>

      <aside
        data-tone={sidebarTone}
        className={[
          // The width slides; the contents switch at once and the overflow is cut while it moves.
          "sticky top-0 hidden h-dvh shrink-0 flex-col overflow-hidden border-r border-line bg-paper transition-[width] duration-200 ease-out motion-reduce:transition-none lg:flex",
          collapsed ? "w-[68px]" : "w-[260px]",
        ].join(" ")}
      >
        <ShellNav
          collapsed={collapsed}
          onToggleCollapsed={toggleCollapsed}
          brand={brand}
          business={business}
          groups={groups}
          bottom={bottom}
          user={user}
          signOut={signOut}
          places={places}
          navExtra={navExtra}
        />
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {banner}
        <TopBar
          ref={menuButton}
          title={title}
          active={business?.active ?? null}
          user={user}
          open={drawerOpen}
          onOpen={() => setDrawerOpen(true)}
        />
        <main id="main" className="min-w-0 flex-1">
          {/* A page with `data-page-width="full"` on its root (the Calendar) drops the 1200px limit. */}
          <div className="mx-auto w-full max-w-[1200px] px-5 py-6 has-[[data-page-width=full]]:max-w-none lg:px-10 lg:py-8">{children}</div>
        </main>
      </div>

      <Drawer open={drawerOpen} onClose={closeDrawer} returnTo={menuButton} tone={sidebarTone}>
        <ShellNav
          brand={brand}
          places={places}
          business={business}
          groups={groups}
          bottom={bottom}
          user={user}
          signOut={signOut}
          navExtra={navExtra}
          onNavigate={closeDrawer}
        />
      </Drawer>

      <ToastViewport />
    </div>
  );
}

/** The sidebar's contents, which the drawer shows too. */
function ShellNav({
  brand,
  business,
  groups,
  bottom,
  user,
  signOut,
  places,
  navExtra,
  onNavigate,
  collapsed = false,
  onToggleCollapsed,
}: {
  brand: string;
  business?: ShellBusiness;
  groups: ShellNavGroup[];
  bottom: ShellNavItem[];
  user: ShellUser | null;
  signOut?: ShellSignOut;
  places?: ShellPlaces;
  navExtra?: ReactNode;
  /** Called when a link is followed. The drawer closes itself with it. */
  onNavigate?: () => void;
  /** The sidebar is a rail of icons: each link keeps its name for a screen reader and a tooltip. */
  collapsed?: boolean;
  /** The collapse button. The sidebar has it, the drawer does not. */
  onToggleCollapsed?: () => void;
}) {
  const toggle = onToggleCollapsed ? (
    <button
      type="button"
      onClick={onToggleCollapsed}
      aria-label={collapsed ? "Expand the menu" : "Collapse the menu"}
      title={collapsed ? "Expand the menu" : "Collapse the menu"}
      aria-expanded={!collapsed}
      className="flex size-8 shrink-0 items-center justify-center rounded-lg text-ink-3 transition-colors duration-150 hover:bg-surface hover:text-ink"
    >
      {collapsed ? <ChevronRightIcon size={17} /> : <ChevronLeftIcon size={17} />}
    </button>
  ) : null;

  return (
    <div className="flex h-full min-h-0 flex-col">
      {collapsed ? (
        <div className="flex flex-col items-center gap-2 pt-5 pb-4">
          <BrandMark className="size-9" />
          {toggle}
        </div>
      ) : (
        <div className="flex items-center gap-3 px-5 pt-5 pb-4">
          <BrandMark className="size-9" />
          <div className="min-w-0 flex-1">
            <p className="text-[17px] leading-tight font-semibold text-ink">Teamna</p>
            <p className="truncate text-[12px] text-ink-3">{brand}</p>
          </div>
          {toggle}
        </div>
      )}

      {business ? (
        collapsed ? (
          business.active ? (
            <div className="flex justify-center pb-4" title={business.active.name}>
              <BusinessMark business={business.active} size={36} />
            </div>
          ) : null
        ) : (
          <BusinessSwitcher business={business} />
        )
      ) : null}

      <div className={["min-h-0 flex-1 overflow-y-auto pb-3", collapsed ? "px-2" : "px-3"].join(" ")}>
        <nav aria-label="Primary">
          {groups.map((group, index) => (
            <div key={group.label ?? index} className={index === 0 ? "" : "mt-2"}>
              {group.label && collapsed ? (
                <hr className="mx-2 mb-2 border-line" />
              ) : group.label ? (
                <p className="px-3 pt-2 pb-1.5 text-[11px] font-bold tracking-[0.12em] text-ink-3 uppercase">
                  {group.label}
                </p>
              ) : null}
              <ul className="flex flex-col gap-0.5">
                {group.items.map((item) => (
                  <li key={item.to}>
                    <NavLink item={item} onNavigate={onNavigate} iconOnly={collapsed} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
        {/* In the scrolling area, after the last group, so it never takes room from the menu. */}
        {navExtra && !collapsed ? (
          // A click on a link in the card bubbles here, so the drawer closes like after a menu link.
          // oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
          <div className="mt-4" onClick={(event) => (event.target as Element).closest("a") && onNavigate?.()}>
            {navExtra}
          </div>
        ) : null}
      </div>

      <div className={["border-t border-line pt-3 pb-4", collapsed ? "px-2" : "px-3"].join(" ")}>
        {bottom.length > 0 ? (
          <ul className="mb-3 flex flex-col gap-0.5">
            {bottom.map((item) => (
              <li key={item.to}>
                <NavLink item={item} onNavigate={onNavigate} iconOnly={collapsed} />
              </li>
            ))}
          </ul>
        ) : null}
        {user && collapsed ? (
          // The account menu needs the width: expand the menu to switch to another place.
          <div className="mb-3 flex justify-center" title={user.name}>
            <Avatar name={user.name} photoUrl={user.photoUrl ?? null} />
          </div>
        ) : user && places && places.items.length > 1 ? (
          <AccountMenu user={user} places={places} onNavigate={onNavigate} />
        ) : user ? (
          <div className="mb-3 flex items-center gap-3 px-3">
            <Avatar name={user.name} photoUrl={user.photoUrl ?? null} />
            <div className="min-w-0">
              <p className="truncate text-[14px] font-semibold text-ink">{user.name}</p>
              {user.detail ? <p className="truncate text-[12px] text-ink-3">{user.detail}</p> : null}
            </div>
          </div>
        ) : null}
        {signOut ? <SignOutRow onSignOut={signOut.onSignOut} subtitle={signOut.subtitle} iconOnly={collapsed} /> : <SignOutRow iconOnly={collapsed} />}
      </div>
    </div>
  );
}

/**
 * The account block as a button, for an account with two or more places: it opens "Switch to"
 * above it (prototype `teamna/prototypes/account-switch.html`). Escape or a click outside closes it.
 */
function AccountMenu({ user, places, onNavigate }: { user: ShellUser; places: ShellPlaces; onNavigate?: () => void }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (event: PointerEvent) => {
      if (box.current && !box.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  return (
    <div ref={box} className="relative mb-3">
      {open ? (
        <div role="menu" aria-label="Switch to" className="absolute inset-x-0 bottom-full z-30 mb-2 rounded-card border border-line-strong bg-paper p-2 shadow-pop">
          <p className="px-2 pt-1 pb-1.5 text-[11px] font-bold tracking-[0.1em] text-ink-3 uppercase">Switch to</p>
          <PlaceRows
            places={places.items}
            currentId={places.currentId}
            onPick={(place) => {
              setOpen(false);
              onNavigate?.();
              if (place.id !== places.currentId) places.onPick(place);
            }}
          />
        </div>
      ) : null}
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className={[
          "grid w-full grid-cols-[36px_minmax(0,1fr)_auto] items-center gap-3 rounded-xl border px-3 py-2 text-left",
          open ? "border-accent" : "border-line hover:border-line-strong",
        ].join(" ")}
      >
        <Avatar name={user.name} photoUrl={user.photoUrl ?? null} />
        <span className="min-w-0">
          <span className="block truncate text-[14px] font-semibold text-ink">{user.name}</span>
          <span className="block truncate text-[12px] text-ink-3">Switch to another place</span>
        </span>
        <span aria-hidden="true" className="text-[12px] text-ink-3">
          {open ? "▾" : "▴"}
        </span>
      </button>
    </div>
  );
}

/** The look of a menu row: the same for a link of the menu and a row of `ShellNavPreview`. */
function navRowClass(active: boolean, iconOnly: boolean): string {
  return [
    "relative flex min-h-9 items-center gap-2.5 rounded-lg text-[14px] font-semibold transition-colors duration-150",
    iconOnly ? "justify-center px-0" : "px-3",
    active ? "bg-accent-surface text-accent-strong" : "text-ink-2 hover:bg-surface hover:text-ink active:bg-sunken",
  ].join(" ");
}

/** `iconOnly`: the collapsed sidebar. The name stays for a screen reader and shows as a tooltip; a count is a dot. */
function NavLink({ item, onNavigate, iconOnly = false }: { item: ShellNavItem; onNavigate?: () => void; iconOnly?: boolean }) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const active = [item.to, ...(item.also ?? [])].some((to) => pathname === to || pathname.startsWith(`${to}/`));
  const Icon = item.icon;

  return (
    <Link
      to={item.to}
      onClick={onNavigate}
      aria-current={active ? "page" : undefined}
      title={iconOnly ? (item.locked ? `${item.label}, not in your plan` : item.label) : undefined}
      className={navRowClass(active, iconOnly)}
    >
      <Icon size={19} className={active ? "text-accent" : "text-ink-3"} />
      <span className={iconOnly ? "sr-only" : "min-w-0 flex-1 truncate"}>{item.label}</span>
      {iconOnly ? (
        <>
          {item.badge ? (
            <span className="absolute top-1.5 right-2.5 size-2 rounded-full bg-cancelled">
              <span className="sr-only">{item.badge > 99 ? "99+" : item.badge}</span>
            </span>
          ) : null}
          {item.locked ? <LockIcon size={11} className="absolute right-2 bottom-1 text-ink-3" aria-label="Not in your plan" /> : null}
        </>
      ) : (
        <NavLinkEnd item={item} />
      )}
    </Link>
  );
}

/** The count and the lock after the name, in the wide sidebar and the drawer. */
function NavLinkEnd({ item }: { item: ShellNavItem }) {
  return (
    <>
      {item.badge ? (
        <span className="flex h-5 min-w-5 items-center justify-center rounded-pill bg-cancelled px-1.5 text-[11px] font-semibold text-inverse tabular-nums">
          {item.badge > 99 ? "99+" : item.badge}
        </span>
      ) : null}
      {item.locked ? <LockIcon size={14} className="text-ink-3" aria-label="Not in your plan" /> : null}
    </>
  );
}

/**
 * The menu of the shell as a picture, not links: the same groups and rows, for a preview of what a
 * person sees (Settings, Front desk). `rowClassName` adds a look to one row, for example a short
 * highlight on a row that just came or went; `activeTo` lights one row.
 */
export function ShellNavPreview({
  groups,
  activeTo,
  rowClassName,
}: {
  groups: ShellNavGroup[];
  activeTo?: string;
  rowClassName?: (item: ShellNavItem) => string;
}) {
  return (
    <div className="flex flex-col">
      {groups.map((group, index) => (
        <div key={group.label ?? index} className={index === 0 ? "" : "mt-2"}>
          {group.label ? <p className="px-3 pt-2 pb-1.5 text-[11px] font-bold tracking-[0.12em] text-ink-3 uppercase">{group.label}</p> : null}
          <ul className="flex flex-col gap-0.5">
            {group.items.map((item) => {
              const Icon = item.icon;
              const active = item.to === activeTo;
              return (
                <li key={item.to} data-nav={item.to} className={[navRowClass(active, false), rowClassName?.(item) ?? ""].join(" ")}>
                  <Icon size={19} className={active ? "text-accent" : "text-ink-3"} />
                  <span className="min-w-0 flex-1 truncate">{item.label}</span>
                  <NavLinkEnd item={item} />
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}

/**
 * The active business, with a switcher when the account runs more than one.
 *
 * A native select: two businesses is the realistic ceiling, and the browser's own control is
 * the one that already works with a keyboard, a screen reader and a thumb.
 */
function BusinessSwitcher({ business }: { business: ShellBusiness }) {
  const { active, options, onSelect } = business;

  return (
    <div className="px-5 pb-4">
      <p className="text-[11px] font-bold tracking-[0.12em] text-ink-3 uppercase">Business</p>
      <div className="mt-1.5 flex items-center gap-2.5">
        {active ? <BusinessMark business={active} size={36} /> : null}
        {options.length > 1 ? (
          <Select
            label="Which business to manage"
            className="min-w-0 flex-1"
            value={active?.businessId ?? ""}
            onChange={(event) => onSelect(event.target.value)}
          >
            {options.map((option) => (
              <option key={option.businessId} value={option.businessId}>
                {option.name}
              </option>
            ))}
          </Select>
        ) : (
          <p className="min-w-0 flex-1 truncate text-[15px] font-semibold text-ink">
            {active?.name ?? "No business yet"}
          </p>
        )}
      </div>
    </div>
  );
}

function Avatar({ name, photoUrl }: { name: string; photoUrl?: string | null }) {
  return <PersonAvatar name={name} photoUrl={photoUrl ?? null} size={36} tone="bg-accent-surface text-accent-strong" />;
}

/** Below 1024px: the menu button, the business mark and name, the avatar. */
function TopBar({
  ref,
  title,
  active,
  user,
  open,
  onOpen,
}: {
  ref: RefObject<HTMLButtonElement | null>;
  title: string;
  /** The business the title names. Its mark sits before the name. */
  active: MyBusiness | null;
  user: ShellUser | null;
  open: boolean;
  onOpen: () => void;
}) {
  return (
    <header className="pt-safe sticky top-0 z-30 border-b border-line bg-paper/92 backdrop-blur-md lg:hidden">
      <div className="flex h-(--topbar-height) items-center gap-2 px-3">
        <button
          ref={ref}
          type="button"
          onClick={onOpen}
          aria-label="Open menu"
          aria-expanded={open}
          aria-controls="app-drawer"
          className="flex size-11 shrink-0 items-center justify-center rounded-full text-ink transition-colors duration-150 active:bg-sunken"
        >
          <MenuIcon size={22} />
        </button>
        <div className="flex min-w-0 flex-1 items-center justify-center gap-2">
          {active ? <BusinessMark business={active} size={26} /> : null}
          <p className="min-w-0 truncate text-[15px] font-semibold text-ink">{title}</p>
        </div>
        <span className="flex size-11 shrink-0 items-center justify-center">
          {user ? <Avatar name={user.name} photoUrl={user.photoUrl ?? null} /> : null}
        </span>
      </div>
    </header>
  );
}

/**
 * The navigation below 1024px, sliding in from the left over the page.
 *
 * Mounted only while open, so the page never carries a second "Sign out" and a second nav in
 * the accessibility tree.
 */
function Drawer({
  open,
  onClose,
  returnTo,
  tone,
  children,
}: {
  open: boolean;
  onClose: () => void;
  /** The menu button, which gets focus back when the drawer closes. */
  returnTo: RefObject<HTMLButtonElement | null>;
  /** The sidebar's tone, so the drawer and the sidebar are the same picture. */
  tone: "light" | "dark";
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useFocusTrap(ref, open, onClose, returnTo);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-40 lg:hidden">
      <button
        type="button"
        tabIndex={-1}
        aria-label="Close menu"
        onClick={onClose}
        className="absolute inset-0 bg-ink/35 backdrop-blur-[2px]"
      />
      <dialog
        ref={ref}
        open
        id="app-drawer"
        data-tone={tone}
        aria-modal="true"
        aria-label="Menu"
        tabIndex={-1}
        // The element's own margin, border, padding and sizing are reset; the parent lays it out.
        className="pt-safe relative m-0 flex h-full max-h-none w-[300px] max-w-[85vw] flex-col border-0 bg-paper p-0 text-ink shadow-[8px_0_40px_-16px_rgba(16,25,23,0.45)] outline-none motion-safe:animate-drawer"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close menu"
          className="absolute top-4 right-3 flex size-9 items-center justify-center rounded-full border border-line text-ink-2 transition-colors duration-150 active:bg-sunken"
        >
          <CloseIcon size={17} />
        </button>
        {children}
      </dialog>
    </div>
  );
}
